/* eslint-disable @typescript-eslint/naming-convention */
// types
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { CartContext } from "@cart-client/contexts/CartContext";
import { MROCartState } from "@cart-client/reducers/MROCartReducer";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
import {
    MROChildCartState,
    MROChildLocalCart,
    MROTab,
    MRONativeLocalCart,
    MROChildCart,
} from "@swiggy-private/cart-types";
import { MROReviewedCart, MROConfigurationData } from "@cart-client/interfaces/MROFoodCart";

// helpers
import { isEmpty, cloneDeep } from "@swiggy-private/js-utils";
import {
    getMROChildCartByRestaurantId,
    getMROChildCartItemsCount,
    getMROChildCartRestaurantId,
    getMROChildCartRestaurantName,
    getMROLocalCartItemsCount,
    isEmptyMROCart,
    normaliseId,
    logMROEventsIntoNR,
} from "@cart-client/helpers/MROFoodCart";
import { useMROTabsFrequencyCapping } from "./useMROTabsFrequencyCapping";
import { isQuickCheckoutFlow } from "@swiggy-private/checkout-core";

export enum TABErrorType {
    LAST_TAB_REMOVAL = "LAST_TAB_REMOVAL",
}

const logTabErrorsIntoNR = (error: string): never => {
    logMROEventsIntoNR({
        type: "mro_tabs_error",
        error,
    });
    throw new Error(error);
};

export const useMROTabHandler = () => {
    const [tabs, setTabs] = useState<MROTab[]>([]);
    const [tabError, setTabError] = useState<TABErrorType | null>(null);

    const initTabComputeDone = useRef(false);
    const { cartState } = useContext(CartContext);
    const { doUpdateCart } = useContext(UpdateCartContext);
    const { localCart, reviewedCart } = cartState as MROCartState;
    const [mroConfigurationData, setMroConfigurationData] = useState<MROConfigurationData | undefined>(undefined);
    const configLoaded = useRef(false);
    const showFtue = useMROTabsFrequencyCapping(mroConfigurationData);

    /**
     * This is an internal method written to compute the MRO tabs based on the local cart and reviewed cart.
     */
    const computeMROTabs = useCallback((localCart: MRONativeLocalCart | null, reviewedCart: MROReviewedCart | null) => {
        if (!isEmptyMROCart(localCart) && !isEmptyMROCart(reviewedCart?.data)) {
            // loop over local cart and if present in selectedRxIdList, add to tabs
            const selectedTabs: MROTab[] = [];

            localCart?.child_carts?.forEach((child: MROChildLocalCart) => {
                const rxId = String(getMROChildCartRestaurantId(child));

                const serverCart = getMROChildCartByRestaurantId(reviewedCart, rxId);
                // if local child cart is not present in reviewed child cart, then keep old tab data but with unselected state
                if (isEmpty(serverCart)) {
                    selectedTabs.push({
                        restaurantId: Number(rxId),
                        restaurantName: getMROChildCartRestaurantName(child),
                        order_total: child?.order_total ?? 0,
                        itemCount: getMROLocalCartItemsCount(child),
                        cartId: child?.cart_id,
                        originalCartId: child?.originalCartId,
                        selected: MROChildCartState.UNSELECTED,
                    });
                } else {
                    // if server child cart is present push the tab data with selected state while picking the server child cart data
                    selectedTabs.push({
                        restaurantId: Number(rxId),
                        restaurantName: getMROChildCartRestaurantName(child),
                        order_total: serverCart?.order_total ?? 0,

                        itemCount: getMROChildCartItemsCount(serverCart),
                        cartId: serverCart?.cartId,
                        originalCartId: serverCart?.originalCartId,
                        selected: child.cart_state ?? MROChildCartState.UNSELECTED,
                    } as MROTab);
                }
            });
            setTabs(selectedTabs.length > 1 ? selectedTabs : []);
            // mark that initial tab computation as done
            if (!initTabComputeDone.current) {
                const tabsFrequencyCapping =
                    reviewedCart?.data?.mro_configuration_data?.mro_tabs_ftue_configuration_data;

                if (tabsFrequencyCapping) {
                    setMroConfigurationData({
                        counterMaxCount: tabsFrequencyCapping.counterMaxCount ?? 0,
                        counterResetInDays: tabsFrequencyCapping.counterResetInDays ?? 0,
                        lifetimeMaxCount: tabsFrequencyCapping.lifetimeMaxCount ?? 0,
                    });
                    // configLoaded marks the FTUE config was actually captured, separate from
                    // initTabComputeDone which only marks that this block ran once.
                    configLoaded.current = true;
                }
                initTabComputeDone.current = true;
            }
        }
    }, []);

    const isUserRemovingLastTab = useCallback((tab: MROTab, selected_rxids?: string[] | null) => {
        return (
            selected_rxids?.length === 1 &&
            tab.selected === MROChildCartState.SELECTED &&
            selected_rxids.includes(normaliseId(tab.restaurantId))
        );
    }, []);

    const handleTabSelect = useCallback(
        async (tab: MROTab) => {
            const selectedRXList = [...(localCart?.selected_rxids || [])];
            const updatedChildCarts = cloneDeep(localCart?.child_carts || []);
            // get the selected child cart from list of child carts
            const toggleRXChildCart = updatedChildCarts.find(
                (child: MROChildLocalCart) => getMROChildCartRestaurantId(child) === normaliseId(tab.restaurantId),
            );

            // check if user is trying to remove the last tab, show error and return
            if (isUserRemovingLastTab(tab, localCart?.selected_rxids)) {
                setTabError(TABErrorType.LAST_TAB_REMOVAL);
                return;
            }

            if (isEmpty(toggleRXChildCart)) {
                logTabErrorsIntoNR("No child cart found for MRO cart tab selection for tab select");
            }

            setTabError(null);

            // if the tab is unselected, add the restaurant id to the selected RX list
            if (tab.selected === MROChildCartState.UNSELECTED) {
                // add the restaurant id to the selected RX list
                selectedRXList?.push(String(tab.restaurantId));
                toggleRXChildCart.cart_state = MROChildCartState.SELECTED;
            } else if (tab.selected === MROChildCartState.SELECTED) {
                // remove the restaurant id from the selected RX list
                const removalIdx = selectedRXList?.indexOf(String(tab.restaurantId));
                if (removalIdx === -1) {
                    logTabErrorsIntoNR("Restaurant id not found in selected RX list for MRO cart tab unselection");
                }
                selectedRXList?.splice(removalIdx, 1);
                toggleRXChildCart.cart_state = MROChildCartState.UNSELECTED;
            }
            await doUpdateCart({
                ...localCart,
                selected_rxids: selectedRXList,
                child_carts: updatedChildCarts,
            });
        },
        [isUserRemovingLastTab, localCart, doUpdateCart],
    );

    // compute the selected tabs on mount
    useEffect(() => {
        computeMROTabs(localCart, reviewedCart);
    }, [computeMROTabs, localCart, reviewedCart]);

    const handleBulkCartTabSelection = useCallback(async () => {
        const bulkChildCart = reviewedCart?.data?.child_carts?.filter((child) => child.is_bulk_cart);
        if (isEmpty(bulkChildCart)) return;
        const sortBulkCartByHighestOrderTotal = bulkChildCart?.sort((a, b) => b.order_total - a.order_total);
        const pickedBulkCart = sortBulkCartByHighestOrderTotal?.[0];
        if (isEmpty(pickedBulkCart)) return;

        const bulkRestaurantId = getMROChildCartRestaurantId(pickedBulkCart);
        let updatedChildCarts = (localCart?.child_carts ?? []).map((child) => ({
            ...child,
            cart_state:
                getMROChildCartRestaurantId(child) === bulkRestaurantId
                    ? MROChildCartState.SELECTED
                    : MROChildCartState.UNSELECTED,
        })) as MROChildCart[];

        // for quick checkout flow if user chooses to select only large we need to drop other carts from payload
        if (isQuickCheckoutFlow()) {
            updatedChildCarts = updatedChildCarts.filter((cart) => cart.cart_state === MROChildCartState.SELECTED);
        }

        await doUpdateCart({
            ...localCart,
            selected_rxids: [bulkRestaurantId],
            child_carts: updatedChildCarts,
        });
    }, [localCart, reviewedCart, doUpdateCart]);

    return {
        tabs,
        initTabComputeDone: initTabComputeDone.current,
        handleTabSelect,
        handleBulkCartTabSelection,
        tabError,
        showFtue,
    };
};
